export const defaultLocale = 'de-DE';
const defaultOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const defaultFormatString = 'dd.mm.yy';

export const DATE_PRESETS = {
  MEDIUM: { ...defaultOptions },
  WEEKDAY: getMergeFormatOptions({ weekday: 'long' }),
  DATETIME: {
    ...defaultOptions,
    hour: '2-digit',
    minute: '2-digit',
    // Render the stored wall-clock verbatim instead of converting to the
    // browser's local zone. Log dates are stored as UTC instants; migrated
    // entries are date-only (00:00 UTC) and would otherwise shift to 01:00/02:00
    // (CET/CEST). This matches the "Datum mit Uhrzeit, ohne Timezone" requirement.
    timeZone: 'UTC',
  },
} as const;

export type DatePresets = keyof typeof DATE_PRESETS;

export function toReadableDateString(
  dateOrTimestamp?: Date | string | null,
  locale = defaultLocale,
  options = defaultOptions,
): string | undefined {
  if (!dateOrTimestamp) {
    return;
  }

  try {
    const date = toDate(dateOrTimestamp);
    const mergedOptions = getMergeFormatOptions(options);
    return date?.toLocaleDateString(locale, mergedOptions);
  } catch {
    return;
  }
}

export function formatAsISODate(date: Date | string | undefined) {
  if (!date) {
    return;
  }

  if (!(date instanceof Date)) {
    return date;
  }

  const year = date.getFullYear();
  const month = padSegment(date.getMonth() + 1);
  const day = padSegment(date.getDate());

  return `${year}-${month}-${day}`;
}

export function toDate(date?: string | Date | null) {
  if (!date) {
    return;
  }

  if (typeof date !== 'string') {
    return date;
  }

  if (date.trim().length === 0) {
    return;
  }

  try {
    // Parse "YYYY-MM-DD" LocalDate strings as local midnight to avoid a
    // timezone shift via UTC-midnight parsing. Fall back to native parsing
    // for date-time strings (with "T") and other formats.
    const localDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
    const parsedDate = localDateMatch
      ? new Date(
          Number(localDateMatch[1]),
          Number(localDateMatch[2]) - 1,
          Number(localDateMatch[3]),
        )
      : new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return;
    }
    return parsedDate;
  } catch {
    return;
  }
}

export function getMergeFormatOptions(
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
  return { ...defaultOptions, ...options };
}

export function toFormatString(options?: Intl.DateTimeFormatOptions) {
  if (!options) {
    return defaultFormatString;
  }

  let format = '';
  const separator = '.';

  switch (options.weekday) {
    case 'long':
      format += 'DD, ';
      break;
    case 'short':
      format += 'd, ';
      break;
  }

  switch (options.day) {
    case '2-digit':
      format += 'dd';
      break;
    case 'numeric':
      format += 'd';
      break;
  }

  if (format.length > 0 && (options.month || options.year)) {
    format += separator;
  }

  switch (options.month) {
    case '2-digit':
      format += 'mm';
      break;
    case 'numeric':
      format += 'm';
      break;
    case 'short':
      format += 'M';
      break;
    case 'long':
      format += 'MM';
      break;
  }

  if (options.month && options.year) {
    format += separator;
  }

  switch (options.year) {
    case 'numeric':
      format += 'yy';
      break;
    case '2-digit':
      format += 'y';
      break;
  }

  return format || defaultFormatString;
}

// null means "cleared" and must reach the API; undefined means "not sent".
export function toGraphQlLocalDate(date: Date): string;
export function toGraphQlLocalDate(date: Date | null): string | null;
export function toGraphQlLocalDate(
  date: Date | null | undefined,
): string | null | undefined;
export function toGraphQlLocalDate(
  date: Date | null | undefined,
): string | null | undefined {
  if (date === null) return null;
  if (!date) return undefined;
  return formatAsISODate(date);
}

const typedGermanDate = /(\d{1,2})\.(\d{1,2})\.(\d{4}|\d{2})(?!\d)/;

// Reads the first dd.mm.yy(yy) in the text, so an optional weekday prefix is ignored.
export function parseGermanDateInput(text: string): Date {
  const match = typedGermanDate.exec(text);
  if (!match) throw new Error(`Invalid date: ${text}`);

  const day = Number(match[1]);
  const month = Number(match[2]) - 1;
  const year = Number(match[3].length === 2 ? `20${match[3]}` : match[3]);
  const date = new Date(year, month, day);
  if (date.getDate() !== day || date.getMonth() !== month) {
    throw new Error(`Invalid date: ${text}`);
  }
  return date;
}

function padSegment(value: number): string {
  return value.toString().padStart(2, '0');
}

// Built lazily: the formatter is only needed when a Logbuch entry is written.
let germanWallClockFormatter: Intl.DateTimeFormat | undefined;

export function germanWallClockNow(instant = new Date()): Date {
  germanWallClockFormatter ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // h23 keeps midnight at "00" instead of the "24" some locales render.
    hourCycle: 'h23',
  });

  const parts = germanWallClockFormatter.formatToParts(instant);
  const segment = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return new Date(
    Date.UTC(
      segment('year'),
      segment('month') - 1,
      segment('day'),
      segment('hour'),
      segment('minute'),
      segment('second'),
      // Sub-second precision is zone-independent, so it carries over unchanged.
      instant.getMilliseconds(),
    ),
  );
}

/**
 * The instant a German calendar day (`YYYY-MM-DD`) begins, i.e. midnight
 * Europe/Berlin. `dayOffset` shifts the day, so `1` yields the exclusive end.
 */
export function germanStartOfDayInstant(isoDate: string, dayOffset = 0): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  const utcMidnight = Date.UTC(year, month - 1, day + dayOffset);
  const berlinOffset =
    germanWallClockNow(new Date(utcMidnight)).getTime() - utcMidnight;
  return new Date(utcMidnight - berlinOffset);
}
